import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';

import { type ApiResult, type DiscardEditResult, type HttpStatus } from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';
import { type EmojiId } from '@app/client/common/structs/emoji/emoji';
import { YatPageVersion, type YatPageVersionResponse } from '@app/client/common/structs/yatPage/yat-page';
import { updateCurrentUser } from '@app/client/resources/users';
import { YatPageV2 } from '@app/client/resources/yat-pages';
import config from '@app/config';
import { setUser } from '@app/store/actions/user';

import { type RootState } from '../reducers';
import { setEmptyYatPage, setIsYatPageOwner, setYatPage, setYatPageVersion } from '../slices';

import { setError } from './error';
import { addNotification } from './notification';
import { setCurrentEmojiTags } from './user-emojis';
import { getLiquidIdCollectionItems, getUserSettings, getYatPageItems } from './yat-page-items';

/**
 * This action will hit an endpoint that handles fetching page details for both Yat Page v1 and v2.
 * This means that v1 returns the Tags, while v2 returns Yat Page details (like publish date, etc.).
 *
 * Note:
 *  1. Either v1 or v2 data will be returned (it cannot return both for v1 and v2).
 *  2. The API endpoint works both for public and private pages.
 *
 * Primary purpose of this action is to be able to detect the version of the Yat Page.
 *
 * @param eid {string} Emoji ID
 * @param isPublic
 * @param fetchPageItems
 */
export function getYatPageDetails(eid: EmojiId, isPublic = true, fetchPageItems = false) {
    return async function (
        dispatch: ThunkDispatch<RootState, void, Action>,
        getState: () => RootState,
    ): Promise<ApiResult<YatPageVersionResponse>> {
        const userEmojis = getState().userEmojis.extendedEmojis;
        const emojiWasm = getState().emoji.wasm;
        const currentUserName = getState().user?.userAccount?.user.username;

        return apiFactory()
            .call(YatPageV2.getYatPageDetails, {
                eid,
            })
            .then((r) => {
                if (!r.ok) {
                    dispatch(setYatPageVersion(YatPageVersion.NOT_FOUND));
                }
                if (r?.ok && r?.value?.version) {
                    let userOwnsYat: boolean;

                    if (emojiWasm) {
                        userOwnsYat = !!userEmojis.find(
                            (e) =>
                                new emojiWasm.EmojiId(e.display_format, config.maxLengthYat)?.canonical() ===
                                eid.toString(),
                        );
                    } else {
                        // in case wasm isn't loaded
                        userOwnsYat = !!userEmojis.find((e) => e.canonical_format === eid.toString());
                    }

                    dispatch(setIsYatPageOwner(userOwnsYat));
                    switch (r.value.version) {
                        case YatPageVersion.V2: {
                            dispatch(setYatPage(r.value.v2));
                            const { heading: username } = r.value.v2;
                            if (userOwnsYat && !currentUserName) {
                                if (username?.length) {
                                    apiFactory()
                                        .call(updateCurrentUser, { value: { username } })
                                        .then((r) => {
                                            if (r.ok && r.value) {
                                                dispatch(setUser(r.value));
                                            }
                                        });
                                }
                            }

                            if (!fetchPageItems) break;
                            dispatch(getYatPageItems(eid, r.value.v2.id, userOwnsYat && !isPublic));
                            dispatch(getUserSettings());
                            dispatch(
                                getLiquidIdCollectionItems({
                                    collectionId: r.value.v2.liquid_id,
                                    eid: r.value.v2.eid,
                                    pageId: null,
                                }),
                            );
                            break;
                        }
                        case YatPageVersion.V1: {
                            if (r.value.v1.status) {
                                dispatch(setCurrentEmojiTags(r.value.v1.result || []));
                            } else {
                                dispatch(setCurrentEmojiTags([]));
                                dispatch(setError({ error: `Error fetching tags for ${eid}`, source: 'UserEmojis' }));
                            }
                            break;
                        }
                    }
                    dispatch(setYatPageVersion(r.value.version));
                } else {
                    // eslint-disable-next-line no-console
                    console.warn(`No version found for ${eid}`);
                }
                return r;
            })
            .catch((e) => {
                dispatch(setEmptyYatPage());
                dispatch(setCurrentEmojiTags([]));
                dispatch(setError({ error: e, source: 'UserEmojis - getYatPageDetails()' }));
                return e;
            });
    };
}

export function convertYatPage(eid: EmojiId) {
    return function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<ApiResult<HttpStatus>> {
        return apiFactory()
            .call(YatPageV2.convertYatPage, {
                value: {},
                eid: eid,
            })
            .then((r) => {
                dispatch(
                    addNotification({
                        message: 'Your Yat page has been converted!',
                        severity: 'success',
                    }),
                );

                // Give the server a second to process the new page
                setTimeout(() => {
                    dispatch(getYatPageDetails(eid));
                }, 1000);
                return r;
            })
            .catch((e) => {
                return e;
            });
    };
}

export function createYatPage(eid: EmojiId) {
    return function (): Promise<ApiResult<HttpStatus>> {
        return apiFactory()
            .call(YatPageV2.createYatPage, { value: { eid } })
            .catch((e) => {
                return e;
            });
    };
}

export function discardYatPageEdits(pageId: string) {
    return function (): Promise<ApiResult<DiscardEditResult>> {
        return apiFactory()
            .call(YatPageV2.discardYatPageEdits, {
                value: {},
                pageId,
            })
            .catch((e) => {
                return e;
            });
    };
}
